/** Coordinates backend wake-up while keeping browser traffic on its stable URL. */
import env from '../config/environment';

type WakeOptions = { force?: boolean };

export type CodeSwarmWakeClient = {
  backendUrl: string;
  wakeStatusUrl: string;
  ensureBackendReady: (options?: WakeOptions) => Promise<unknown>;
  request: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
};

type CodeSwarmWakeClientApi = {
  loadCodeSwarmWakeClient: () => Promise<CodeSwarmWakeClient> | CodeSwarmWakeClient;
};

declare global {
  interface Window {
    CodeSwarmWakeClient?: CodeSwarmWakeClientApi;
  }
}

interface LegacyWakeResponse {
  status?: 'started' | 'starting' | 'ready' | 'extended' | 'alive';
}

const LEGACY_WAKE_STATUSES = new Set<LegacyWakeResponse['status']>([
  'started', 'starting', 'ready', 'extended', 'alive',
]);

export class BackendManager {
  private isBackendReady = false;
  private startupPromise: Promise<void> | null = null;
  private wakeClientPromise: Promise<CodeSwarmWakeClient> | null = null;
  private forceNextReadinessCheck = false;
  private healthCheckAttempted = false;

  getApiBaseUrl(): string {
    return env.apiBaseUrl;
  }

  async checkHealth(): Promise<boolean> {
    if (!this.usesWakeClient()) return true;
    this.healthCheckAttempted = true;
    try {
      await this.ensureBackendReady();
      const timeoutSignal = typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(env.backend.healthCheckTimeout)
        : undefined;
      const request = {
        method: 'GET',
        ...(timeoutSignal ? { signal: timeoutSignal } : {}),
      } satisfies RequestInit;
      const client = await this.getWakeClient();
      const response = client
        ? await client.request(`${env.apiBaseUrl}/health/`, request)
        : await fetch(`${env.apiBaseUrl}/health/`, request);
      this.isBackendReady = response.ok;
      return response.ok;
    } catch {
      this.isBackendReady = false;
      return false;
    }
  }

  isReady(): boolean {
    return this.isBackendReady;
  }

  hasHealthCheckAttempted(): boolean {
    return this.healthCheckAttempted;
  }

  isOnDemandEnabled(): boolean {
    return this.usesWakeClient();
  }

  async ensureBackendReady(): Promise<void> {
    if (!this.usesWakeClient()) return;
    if (!window.CodeSwarmWakeClient?.loadCodeSwarmWakeClient && this.isBackendReady) return;
    if (this.startupPromise) return this.startupPromise;

    this.startupPromise = this.startBackend();
    try {
      await this.startupPromise;
    } finally {
      this.startupPromise = null;
    }
  }

  async manualStart(): Promise<void> {
    await this.ensureBackendReady();
  }

  private usesWakeClient(): boolean {
    return Boolean(window.CodeSwarmWakeClient?.loadCodeSwarmWakeClient || env.apiGatewayStartEndpoint);
  }

  private async startBackend(): Promise<void> {
    if (window.CodeSwarmWakeClient?.loadCodeSwarmWakeClient) {
      const client = await this.getWakeClient();
      if (!client) throw new Error('CodeSwarm wake client failed to load');
      const force = this.forceNextReadinessCheck;
      await client.ensureBackendReady(force ? { force: true } : undefined);
      this.forceNextReadinessCheck = false;
      this.isBackendReady = true;
      return;
    }

    // Compatibility for existing non-CodeSwarm deployments. Never follow a
    // returned task IP: it is unstable and would be mixed content from HTTPS.
    if (!env.apiGatewayStartEndpoint) return;
    const response = await fetch(env.apiGatewayStartEndpoint, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Failed to start backend: ${response.status}`);
    const data = await response.json() as LegacyWakeResponse;
    if (!LEGACY_WAKE_STATUSES.has(data.status)) {
      throw new Error(`Unexpected backend wake status: ${String(data.status)}`);
    }
    await this.waitForBackendReady();
  }

  private getWakeClient(): Promise<CodeSwarmWakeClient> | null {
    const loader = window.CodeSwarmWakeClient?.loadCodeSwarmWakeClient;
    if (!loader) return null;
    if (!this.wakeClientPromise) {
      // Loading reads runtime configuration only. Readiness is established
      // separately so the same client can notice another sleep cycle later.
      this.wakeClientPromise = Promise.resolve(loader()).catch((error) => {
        this.wakeClientPromise = null;
        throw error;
      });
    }
    return this.wakeClientPromise;
  }

  private async waitForBackendReady(): Promise<void> {
    const startTime = Date.now();
    while (Date.now() - startTime < env.backend.startupTimeout) {
      if (await this.checkHealth()) return;
      await new Promise(resolve => setTimeout(resolve, env.backend.pollingInterval));
    }
    this.isBackendReady = false;
    throw new Error('Backend startup timed out before its health check became ready');
  }

  // Retained for callers from the previous lifecycle implementation. There is
  // intentionally no browser keep-alive timer under managed auto-sleep.
  stopKeepAlive(): void {}

  reset(): void {
    this.isBackendReady = false;
    this.startupPromise = null;
    this.forceNextReadinessCheck = true;
  }

  async warmUp(): Promise<void> {
    await this.ensureBackendReady();
  }
}

const backendManager = new BackendManager();
export default backendManager;
